<template>
    <div>
        <v-row>
            <v-col cols="12" md="6" sm="6">
                <v-card class="ma-3">
                    <v-card-title>{{ $t('CreditTransferPaymentRun') }}</v-card-title>
                    <v-card-text>
                        <v-list-item two-line>
                            <v-list-item-content>
                                <v-list-item-title>{{ $d(new Date(paymentRun['createdAt']), 'long') }}</v-list-item-title>
                                <v-list-item-subtitle>{{ $t('createdAt') }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item two-line>
                            <v-list-item-content>
                                <v-list-item-title>{{ $d(new Date(paymentRun['dueAt']), 'long') }}</v-list-item-title>
                                <v-list-item-subtitle>{{ $t('dueAt') }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-card-text>
                    <v-card-title>{{ $t('debtor') }}</v-card-title>
                    <v-card-text v-if="debtor">
                        <v-list-item two-line>
                            <v-list-item-content>
                                <v-list-item-title>{{ debtor.sepaInfo.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ $t('sepaInfo.name') }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item two-line>
                            <v-list-item-content>
                                <v-list-item-title>{{ debtor.sepaInfo.IBAN }}</v-list-item-title>
                                <v-list-item-subtitle>{{ $t('sepaInfo.IBAN') }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item two-line>
                            <v-list-item-content>
                                <v-list-item-title>{{ debtor.sepaInfo.BIC }}</v-list-item-title>
                                <v-list-item-subtitle>{{ $t('sepaInfo.BIC') }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="6" sm="6">
                <v-card class="ma-3">
                    <v-card-text>
                        <template v-if="partyCreated === null">
                            <party-form :values="creditor" :errors="partyViolations"/>
                            <v-btn color="primary" @click="createCreditor">{{ $t('next') }}</v-btn>
                        </template>
                        <template v-else>
                            <credit-transfer-form :values="creditTransfer" :errors="creditViolations"/>
                            <v-btn color="success" @click="createTransfer">{{ $t('add') }}</v-btn>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="12" sm="12">
                <v-card class="ma-3">
                    <v-card-title>
                        {{ $t('creditTransfers') }}:
                        {{ $n(paymentRun['totalAmount']['amount'] / 100, 'currency') }} {{ paymentRun['totalAmount']['currency'] }}
                    </v-card-title>
                    <credit-transfer-table
                        :transfers="transfers"
                        :creditors="creditors"
                    >
                    </credit-transfer-table>
                </v-card>
            </v-col>
        </v-row>
        <loading :visible="isLoading || paymentRunLoading || partyLoading || creditLoading" />
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import { mapFields } from 'vuex-map-fields';
    import Loading from '../../components/Loading';
    import CreditTransferTable from "../../components/credittransfer/Table";
    import PartyForm from "../../components/party/Form";
    import CreditTransferForm from "../../components/credittransfer/Form";

    const servicePrefix = 'AddTransfersToRun';


    export default {
        name: 'AddTransfersToRunCreate',
        servicePrefix,
        components: {
            CreditTransferForm,
            CreditTransferTable,
            Loading,
            PartyForm
        },
        data() {
            return {
                creditor: {
                    sepaInfo: {
                        name: '',
                        IBAN: '',
                        BIC: ''
                    }
                },
                creditTransfer: {
                    creditor: '',
                    amount: {
                        amount: 0.0,
                        currency: 'EUR'
                    },
                    remittanceInfo: '',
                    endToEndId: ''
                }
            };
        },
        computed: {
            ...mapFields('addtransferstorun', ['error', 'isLoading', 'created', 'violations']),
            ...mapFields('credittransferpaymentrun', {
                paymentRunLoading: 'isLoading'
            }),
            ...mapGetters('credittransferpaymentrun', {
                findPaymentRun: 'find',
            }),
            paymentRun: {
                get() {
                    return this.findPaymentRun(decodeURIComponent(this.$route.params.id));
                }
            },
            ...mapGetters('credittransfer', { transfers: 'list' }),
            ...mapFields('credittransfer', {
                creditError: 'error',
                creditLoading: 'isLoading',
                creditCreated: 'created',
                creditViolations: 'violations'
            }),
            ...mapGetters('party', { 'findParty': 'find' }),
            ...mapFields('party', {
                partyError: 'error',
                partyLoading: 'isLoading',
                partyCreated: 'created',
                partyViolations: 'violations'
            }),
            creditors() {
                if (this.transfers) {
                    return this.transfers.map(t => this.findParty(t['creditor']));
                }
                return [];
            },
            debtor() {
                if (this.paymentRun) {
                    return this.findParty(this.paymentRun['debtor']);
                }
                return null;
            }
        },
        methods: {
            ...mapActions('addtransferstorun', ['create']),
            ...mapActions('credittransferpaymentrun', {
                retrievePaymentRun: 'load',
            }),
            ...mapActions('credittransfer', {
                fetchRelatedTransfers: 'fetchRelatedTransfers',
                createCreditTransfer: 'create',
            }),
            ...mapActions('party', {
                fetchParty: 'load',
                createParty: 'create',
            }),
            createCreditor() {
                this.createParty(this.creditor);
            },
            createTransfer() {
                const creditTransfer =  { ...this.creditTransfer };
                creditTransfer['amount']['amount'] = Math.floor(Number.parseFloat(creditTransfer['amount']['amount']) * 100);
                this.createCreditTransfer(this.creditTransfer);
            },
            reset() {
                this.creditor = {
                    sepaInfo: {
                        name: '',
                        IBAN: '',
                        BIC: ''
                    }
                };
                this.creditTransfer = {
                    creditor: '',
                    amount: {
                        amount: 0.0,
                        currency: 'EUR'
                    },
                    remittanceInfo: '',
                    endToEndId: ''
                };
            }
        },
        created() {
            this.retrievePaymentRun(decodeURIComponent(this.$route.params.id))
            this.fetchRelatedTransfers(decodeURIComponent(this.$route.params.id));
        },
        watch: {
            paymentRun(v) {
                if (v) {
                    this.fetchParty(v['debtor']);
                }
            },
            transfers(v) {
                if (v) {
                    for (const t of v) {
                        this.fetchParty(t['creditor']);
                    }
                }
            },
            partyCreated(v) {
                if (v) {
                    this.creditTransfer['creditor'] = v['@id'];
                }
            },
            creditCreated(v) {
                if (v) {
                    this.create({ paymentRun: this.paymentRun['@id'], creditTransfers: [ v['@id'] ]});
                }
            },
            created(v) {
                if (v) {
                    this.creditCreated = null;
                    this.partyCreated = null;
                    this.created = null;
                    this.reset();
                    this.retrievePaymentRun(decodeURIComponent(this.$route.params.id))
                }
            }
        }
    };
</script>
